import { z } from 'zod';
import type { LeadStatus } from './lead-status.js';

/**
 * Rémunération — trois flux d'argent, JAMAIS mélangés (décision du 06/10/2026) :
 *
 *  - `revenue`           : CA Hipto, ce que l'ACHETEUR (`DeliveryPartner`,
 *                          ex. Club Conso, Hueber) paie à Hipto ;
 *  - `payout`            : rétrocession, ce que Hipto paie au RÉSEAU
 *                          (`Partner`, ex. Bolting, LCT) — une grille par réseau,
 *                          avec une ligne par défaut (`partnerId` nul) ;
 *  - `vendor_commission` : ce que le réseau paie à SES vendeurs (lot 5). La
 *                          grille appartient au RP du réseau (`partnerId`
 *                          obligatoire, jamais de ligne par défaut). Hipto ne
 *                          la paie pas : elle n'entre JAMAIS dans le CA, la
 *                          rétrocession ni la marge.
 *
 * Marge = CA − rétrocession. Stocké en TEXT (pas d'enum Postgres) : un
 * `ALTER TYPE … ADD VALUE` exige d'être propriétaire du type, ce qui a déjà
 * bloqué des déploiements dans l'org. Le domaine est verrouillé ici, côté Zod.
 */
export const FINANCE_FLOWS = ['revenue', 'payout', 'vendor_commission'] as const;
export type FinanceFlow = (typeof FINANCE_FLOWS)[number];
export const FinanceFlowSchema = z.enum(FINANCE_FLOWS);

/**
 * Assiette d'une ligne de grille :
 *  - `per_lead` : ancien modèle (avant le 07/09/2026), acquis à la LIVRAISON ;
 *  - `per_sale` : modèle courant, acquis à la vente ACTIVÉE (`sale_confirmed`).
 */
export const TARIFF_BASES = ['per_lead', 'per_sale'] as const;
export type TariffBasis = (typeof TARIFF_BASES)[number];
export const TariffBasisSchema = z.enum(TARIFF_BASES);

/**
 * Bascule du modèle de rémunération : à partir de ce jour (00:00, heure de
 * Paris), la grille est `per_sale`. L'initialisation de la grille depuis les
 * verticales (`POST /finance/tariffs/seed`) part de cette date ; l'historique
 * `per_lead` antérieur relève de la reprise (backfill).
 */
export const TARIFF_PER_SALE_SINCE = '2026-09-07T00:00:00+02:00';

/**
 * Statut d'une ligne du grand livre (`lead_financial_lines`, append-only) :
 *  - `estimated` : pipeline (vente validée, pas encore activée). Une ligne
 *                  négative `estimated` retire l'estimation (activation, refus) ;
 *  - `earned`    : acquis ;
 *  - `reversed`  : contre-passation d'un acquis (ligne NÉGATIVE). Jamais de
 *                  DELETE ni d'UPDATE : on annule en écrivant.
 *
 * Acquis net = Σ(`earned` + `reversed`) ; pipeline net = Σ(`estimated`).
 */
export const FINANCIAL_LINE_STATUSES = ['estimated', 'earned', 'reversed'] as const;
export type FinancialLineStatus = (typeof FINANCIAL_LINE_STATUSES)[number];
export const FinancialLineStatusSchema = z.enum(FINANCIAL_LINE_STATUSES);

/**
 * Origine d'une ligne :
 *  - `tariff`     : montant de grille ;
 *  - `tmk_actual` : montant réel TMK (`remOperator`) d'une ligne de vente telco ;
 *  - `manual`     : correction admin, motif obligatoire — le moteur ne la
 *                   compense JAMAIS (elle s'ajoute à ce qu'il calcule) ;
 *  - `backfill`   : reprise de l'historique (lot ultérieur), traitée comme une
 *                   ligne moteur.
 */
export const FINANCIAL_LINE_SOURCES = ['tariff', 'tmk_actual', 'manual', 'backfill'] as const;
export type FinancialLineSource = (typeof FINANCIAL_LINE_SOURCES)[number];
export const FinancialLineSourceSchema = z.enum(FINANCIAL_LINE_SOURCES);

/** Sources possédées par le moteur : il les réconcilie (tout sauf `manual`). */
export const ENGINE_OWNED_LINE_SOURCES: readonly FinancialLineSource[] = [
  'tariff',
  'tmk_actual',
  'backfill',
] as const;

/**
 * Statuts où la fiche est **livrée ou au-delà** : seuil d'acquisition d'une
 * ligne `per_lead`. `delivery_failed` et `cancelled` n'en font pas partie —
 * une fiche qui y retombe voit sa ligne contre-passée.
 */
export const DELIVERED_OR_LATER_STATUSES: ReadonlySet<LeadStatus> = new Set<LeadStatus>([
  'delivered',
  'not_sold',
  'sold',
  'sale_validated',
  'sale_confirmed',
  'sale_rejected',
]);

/** Statut « vente estimée » (pipeline) : remontée par le call, pas activée. */
export const ESTIMATED_SALE_STATUSES: ReadonlySet<LeadStatus> = new Set<LeadStatus>([
  'sale_validated',
]);

/** Actions tracées dans `finance_access_logs`. */
export const FINANCE_ACCESS_ACTIONS = [
  'tariffs.list',
  'tariffs.create',
  'tariffs.close',
  'tariffs.replace',
  'tariffs.seed',
  'summary.read',
  'lead_lines.read',
  'lead_lines.manual',
  'my_network.summary',
  'my_network.lines',
  'my_network.export',
  'setup_status.read',
  'vendor_tariffs.list',
  'vendor_tariffs.create',
  'vendor_tariffs.replace',
  'vendor_tariffs.close',
  'vendor_commissions.summary',
  'vendor_commissions.lines',
  'my_commissions.read',
  'my_commissions.lead',
  'visibility_settings.read',
  'visibility_settings.update',
] as const;
export type FinanceAccessAction = (typeof FINANCE_ACCESS_ACTIONS)[number];

/**
 * Partenaire virtuel des comptes admin Hipto (cf. seed, `HierarchyService`).
 * Ses fiches sont des essais : elles ne comptent JAMAIS dans les finances —
 * ni CA, ni rétrocession, ni commission (décision du 09/10/2026).
 */
export const ADMINISTRATION_PARTNER_NAME = 'Administration';

/** La fiche appartient-elle au partenaire Administration (hors finances) ? */
export function isAdministrationPartner(partner: { name?: string | null } | null | undefined): boolean {
  return partner?.name === ADMINISTRATION_PARTNER_NAME;
}
